/**
 * 「この商品はプラン（飲み放題・コース）に含まれるか」を決める唯一の入口。
 *
 * 客側の QR 注文だけがこの判定を持っていて、
 * スタッフの入力とPCの会計は見ていなかった。
 * 夜職は大半がスタッフ入力なので、飲み放題に含まれる酒も
 * 全部そのまま課金され、二重取りになっていた。
 *
 * 継承（basePlanId）があるので、親のプランに含まれる商品も対象になる。
 * 継承を辿らないと、90分プランが60分プランを継承しているときに
 * 「60分には入っているのに90分では課金される」が起きる。
 */

export interface PlanLike {
  id: string;
  includedProductIds?: string[];
  basePlanId?: string | null;
}

export interface PlanOrderLike {
  planId?: string | null;
  /** 会計時に個別調整していれば、そちらを優先する */
  checkoutAdjustment?: { includedProductIds?: string[] } | null;
}

/**
 * プランに含まれる商品IDを、継承をたどって全部集める。
 * 循環参照があっても止まる。
 */
export function resolveIncludedProductIds(
  planId: string | null | undefined,
  plans: PlanLike[],
  seen: Set<string> = new Set()
): Set<string> {
  const out = new Set<string>();
  if (!planId || seen.has(planId)) return out;
  seen.add(planId);

  const plan = plans.find((p) => p.id === planId);
  if (!plan) return out;

  (plan.includedProductIds ?? []).forEach((id) => out.add(id));

  // 継承元も辿る
  if (plan.basePlanId) {
    resolveIncludedProductIds(plan.basePlanId, plans, seen).forEach((id) => out.add(id));
  }
  return out;
}

/**
 * この卓で有効なプランに含まれる商品IDを返す。
 * 卓に複数のプラン（人ごと）が付いていれば、どれかに含まれていれば対象。
 */
export function includedProductIdsForTable(
  planOrders: PlanOrderLike[],
  plans: PlanLike[]
): Set<string> {
  const out = new Set<string>();
  planOrders.forEach((po) => {
    // 会計時に個別調整していれば、そちらが優先
    const override = po.checkoutAdjustment?.includedProductIds;
    if (override) {
      override.forEach((id) => out.add(id));
      return;
    }
    resolveIncludedProductIds(po.planId, plans).forEach((id) => out.add(id));
  });
  return out;
}

/** 商品1点の請求額。プランに含まれていれば0円。 */
export function chargeableAmount(
  productId: string | null | undefined,
  unitPrice: number,
  quantity: number,
  included: Set<string>
): number {
  if (productId && included.has(productId)) return 0;
  return unitPrice * quantity;
}
